import * as ReactDOM from "react-dom/client" // Import from "react-dom/client"
import App from "./App"
import { BrowserRouter } from "react-router-dom"
import { Provider } from "mobx-react"
import Utils from "./utils/utils"
import initializeStores from "./stores/storeInitializer"
import appDataService from "@services/appDataService"
import "./index.css"
import "./styles/custom-bootstrap.less"
import "./styles/custom-ant.less"
import "./styles/app.less"
import { ErrorBoundary } from "@components/ErrorBoundary"
import { ConfigProvider, ThemeConfig } from "antd"
import dayjs from "dayjs"
import updateLocale from "dayjs/plugin/updateLocale"
import localeData from "dayjs/plugin/localeData"

import { QueryClient, QueryClientProvider } from "@tanstack/react-query"

// Cấu hình dayjs
dayjs.extend(updateLocale)
dayjs.extend(localeData)
dayjs.updateLocale("en", {
  weekStart: 1, // Thứ 2 = 1, Chủ nhật = 0
})

const theme: ThemeConfig = {
  token: {
    colorPrimary: '#98221f',
    colorLink: '#98221f',
    borderRadius: 4,
    colorError: "#E7695F",
    colorSuccess: "#52CB67",

    // Linear border radius scale (comfortable = 6px)
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    borderRadiusXS: 2,

    // Typography (Linear: Inter + font-feature-settings applied via CSS)
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: 13,
    fontSizeSM: 12,
    fontSizeLG: 16,
    lineHeight: 1.5715,

    // Surfaces & colors
    colorBgContainer: "#ffffff",
    colorBgLayout: "#F2F4F8",
    colorBgElevated: "#ffffff",
    colorBgSpotlight: "#f7f8f8",

    // Text hierarchy (Linear light mode)
    colorText: "#1a1a1a",
    colorTextSecondary: "#4B5C71",
    colorTextTertiary: "#8a8f98",
    colorTextQuaternary: "#b0b7c3",

    // Borders (Linear light mode)
    colorBorder: "#d0d6e0",
    colorBorderSecondary: "#e6e6e6",

    // Shadows (Linear elevation system)
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
    boxShadowSecondary: "0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.04)",

    // Spacing
    padding: 16,
    paddingSM: 12,
    paddingLG: 24,
    margin: 16,
    marginSM: 12,
    marginLG: 24,
  },
  components: {
    // Table: clean, Linear-style header
    Table: {
      headerBg: "#f7f8f8",
      headerColor: "#4B5C71",
      headerSortActiveBg: "#f0f2f5",
      rowHoverBg: "#fafafa",
      borderColor: "#e6e6e6",
      borderRadius: 8,
      cellPaddingBlock: 10,
      cellPaddingInline: 12,
    },
    // Card: minimal shadow, clean
    Card: {
      borderRadius: 8,
      boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
    },
    // Button: Linear comfortable radius
    Button: {
      borderRadius: 6,
      defaultBorderColor: "#d0d6e0",
      defaultColor: "#1a1a1a",
      fontWeight: 500,
    },
    // Inputs
    Input: {
      borderRadius: 6,
      colorBorder: "#d0d6e0",
    },
    Select: {
      borderRadius: 6,
    },
    InputNumber: {
      borderRadius: 6,
    },
    DatePicker: {
      borderRadius: 6,
    },
    // Modal: Linear dialog shadow
    Modal: {
      borderRadius: 12,
      boxShadow: "0 8px 2px rgba(0,0,0,0), 0 5px 2px rgba(0,0,0,0.01), 0 3px 2px rgba(0,0,0,0.04), 0 1px 1px rgba(0,0,0,0.07), 0 0 1px rgba(0,0,0,0.08)",
    },
    // Dropdown & Menu
    Dropdown: {
      borderRadius: 8,
      boxShadow: "0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.04)",
    },
    Menu: {
      borderRadius: 6,
      itemBorderRadius: 4,
    },
    // Tag: pill style
    Tag: {
      borderRadius: 4,
      defaultBg: "#f3f4f5",
      defaultColor: "#4B5C71",
    },
    // Tooltip
    Tooltip: {
      borderRadius: 6,
      colorBgSpotlight: "#1890FF",
      colorTextLightSolid: "#ffffff",
    },
    // Tabs
    Tabs: {
      inkBarColor: "#1890FF",
      itemActiveColor: "#1890FF",
      itemHoverColor: "#1890FF",
    },
  },
}

const queryClient = new QueryClient()

// 🔥 Chunk Loading Error Handler - Xử lý lỗi khi chunk file không tìm thấy (do deploy mới)
// Khi user đang dùng app và có build mới, các chunk files có thể đã đổi tên
// Handler này sẽ tự động reload trang để load version mới
const setupChunkErrorHandler = () => {
  // Xử lý lỗi khi load script/chunk fail
  window.addEventListener("error", (event) => {
    const errorMessage = event.message || ""
    const errorSource = (event.target as any)?.src || (event.target as any)?.href || ""

    // Kiểm tra nếu là lỗi chunk loading
    const isChunkError =
      errorMessage.includes("chunk") ||
      errorMessage.includes("Loading chunk") ||
      errorMessage.includes("Failed to fetch dynamically imported module") ||
      errorMessage.includes("Loading CSS chunk") ||
      errorSource.includes("static/js/") ||
      errorSource.includes("static/css/")

    if (isChunkError) {
      const reloadKey = "chunk_reload_attempted"
      const lastReload = sessionStorage.getItem(reloadKey)
      const now = Date.now()

      // Chỉ reload nếu chưa reload trong vòng 30 giây (tránh reload loop)
      if (!lastReload || now - Number(lastReload) > 30_000) {
        console.warn("Chunk loading error detected, reloading page...", {
          message: errorMessage,
          source: errorSource,
        })
        sessionStorage.setItem(reloadKey, String(now))
        window.location.reload()
      } else {
        console.error("Chunk loading error persists after reload. Skipping auto-reload to prevent loop.", {
          message: errorMessage,
          source: errorSource,
        })
      }
    }
  })

  // Xử lý lỗi khi promise rejection (dynamic import fail)
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason?.message || event.reason?.toString() || ""

    const isChunkLoadError =
      reason.includes("chunk") ||
      reason.includes("Loading chunk") ||
      reason.includes("Failed to fetch dynamically imported module") ||
      reason.includes("ChunkLoadError")

    if (isChunkLoadError) {
      const reloadKey = "chunk_reload_attempted"
      const lastReload = sessionStorage.getItem(reloadKey)
      const now = Date.now()

      if (!lastReload || now - Number(lastReload) > 30_000) {
        console.warn("Chunk loading promise rejection detected, reloading page...", { reason })
        sessionStorage.setItem(reloadKey, String(now))
        event.preventDefault()
        window.location.reload()
      } else {
        console.error("Chunk load rejection persists after reload. Skipping to prevent loop.", { reason })
      }
    }
  })
}

// Khởi tạo chunk error handler ngay khi app load
setupChunkErrorHandler()

Utils.setLocalization()
appDataService.getAppConfiguration().then(async () => {
  const stores = initializeStores()
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  )
  root.render(
    <Provider {...stores}>
      <ConfigProvider
        theme={theme}
        form={{
          requiredMark: (label, { required }) => {
            // Chỉ hiển thị dấu * nếu required
            if (!required) return label
            return (
              <>
                {label}
                <span className="ml-1">
                  <span style={{ color: "red" }}>*</span>
                </span>
              </>
            )
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </BrowserRouter>
        </QueryClientProvider>
      </ConfigProvider>
    </Provider>
  )

  // registerServiceWorker()
})
